/**
 * Parses a semver version string into major/minor/patch components.
 * Strips leading range operators (^, ~, >=, etc.) before parsing.
 */
export interface SemVer {
  major: number;
  minor: number;
  patch: number;
  raw: string;
}

export function parseSemVer(version: string): SemVer | null {
  const cleaned = version.trim().replace(/^[^0-9]*/, "");
  const match = cleaned.match(/^(\d+)\.(\d+)\.?(\d*)(.*)$/);
  if (!match) return null;

  return {
    major: parseInt(match[1], 10),
    minor: parseInt(match[2], 10),
    patch: match[3] ? parseInt(match[3], 10) : 0,
    raw: cleaned,
  };
}

/**
 * Compares two SemVer values.
 * Returns:
 *  -1 if a < b
 *   0 if a === b
 *   1 if a > b
 */
export function compareSemVer(a: SemVer, b: SemVer): -1 | 0 | 1 {
  if (a.major !== b.major) return a.major > b.major ? 1 : -1;
  if (a.minor !== b.minor) return a.minor > b.minor ? 1 : -1;
  if (a.patch !== b.patch) return a.patch > b.patch ? 1 : -1;
  return 0;
}

/**
 * Returns true if the version string represents a major version upgrade relative to base.
 */
export function isMajorUpgrade(from: string, to: string): boolean {
  const a = parseSemVer(from);
  const b = parseSemVer(to);
  if (!a || !b) return false;
  return b.major > a.major;
}

/**
 * Returns true if the version string represents a minor version upgrade relative to base.
 */
export function isMinorUpgrade(from: string, to: string): boolean {
  const a = parseSemVer(from);
  const b = parseSemVer(to);
  if (!a || !b) return false;
  return b.major === a.major && b.minor > a.minor;
}

/**
 * Strips range operators and returns just the version string.
 */
export function cleanVersion(version: string): string {
  return version.trim().replace(/^[^0-9]*/, "");
}
